import { writeDB, api } from './store';
import { loadAll } from '../util/util';
import { history } from './history';
import { shopPath, shopsPath } from '../routes';
import { snakeCase } from 'lodash';
import { GetParams, PaginatedLoaderResponse, paginatedLoaderResponseFrom } from './data';
import { PaginatedResponse } from './api';
import { parseJsonString } from '../util/data';
import { FileRepresenting } from './asset';
import { Ownership } from './ownerships';

/* INTERFACE */
export interface Shop {
  defaultLocale: string;
  domain?: string | null;
  id: string;
  meta?: string; // JSON string
  name: string;
  paymentBlacklist: string[] | null;
  sitemap: boolean;
  slug: string;
  waitingQueue: boolean;
  orderMergingAllowed: boolean;
  freeShippingThreshold?: number | null;
  logo?: FileRepresenting | null;
  ownerships?: Ownership[] | null;
}

/* SHOP CREATE */
export async function addShop(model: Partial<Shop>): Promise<void> {
  const newShop = await api.createShop({
    ...model,
    meta: parseJsonString(model.meta),
  });
  writeDB.table('shops').upsert(newShop.data);
  history.push(shopPath(newShop.data.slug));
}

/* SHOP READ */
export async function getRawShops({ page, perPage, term }: GetParams): Promise<PaginatedResponse<Shop[]>> {
  const response = await api.indexShops({
    page: (page || 0) + 1,
    perPage,
    term,
  });
  return response as PaginatedResponse<Shop[]>;
}

export async function getShops({
  page,
  perPage,
  term,
  sortBy = 'name',
  sortDir = 'desc',
}: {
  page: number;
  perPage: number;
  term?: string;
  sortBy?: string;
  sortDir?: string;
}): Promise<PaginatedLoaderResponse> {
  const response = await api.indexShops({
    page: (page || 0) + 1,
    perPage,
    term,
    sortBy,
    sortDir,
  });
  writeDB.table('shops').upsert(response.data);
  return paginatedLoaderResponseFrom(response);
}

export async function getAllShops(): Promise<void> {
  const shops = await loadAll((page) => api.indexShops({ page, perPage: 100 }));
  writeDB.table('shops').upsert(shops);
}

export async function getRawShop(shopId: string): Promise<PaginatedResponse<Shop>> {
  const response = await api.showShop({ shopId });
  return response as PaginatedResponse<Shop>;
}

export async function getShop(shopId: string): Promise<void> {
  const response = await api.showShop({ shopId, noCache: true });
  writeDB.table('shops').upsert(response.data);
}

export function getShopName(shopId: string): string {
  const shop = writeDB.table('shops').find(shopId);
  if (shop) {
    return shop.name;
  }
  return '';
}

export function getShopNameWithDomain(shop: { id: string; domain?: string | null; name?: string | null }): string {
  const name = shop.name || shop.id;
  if (shop.domain) {
    return `${name} [${shop.domain}]`;
  }
  return name;
}

/* SHOP UPDATE */
export async function updateShop(shopId: string, model: Partial<Shop>): Promise<void> {
  const newShop = await api.updateShop({
    shopId,
    ...model,
    paymentBlacklist: model.paymentBlacklist && model.paymentBlacklist.map((i) => snakeCase(i)),
    noCache: true,
  });
  writeDB.table('shops').upsert(newShop.data);
}

/* SHOP DELETE */
export async function deleteShop(shopId: string): Promise<void> {
  await api.destroyShop({ shopId });
  writeDB.table('shops').delete(shopId);
  history.push(shopsPath());
}
